import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { BarChart3, Droplets, MapPin, Users } from "lucide-react";

import { formatCLP } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { Card, Cargando, ErrorCarga, PageHeader } from "../../components/ui";
import { MESES } from "../cierres/estado";
import {
  fetchAnalisisConsumo,
  type BalanceHidrico,
  type DistribucionSocios,
  type SectorConsumo,
} from "./api";

const HOY = new Date();

function parseInt2(s: string | null, fallback: number): number {
  const n = Number(s);
  return s && Number.isFinite(n) ? n : fallback;
}

export function AnalisisConsumoPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const mes = parseInt2(sp.get("mes"), HOY.getMonth() + 1);
  const anio = parseInt2(sp.get("anio"), HOY.getFullYear());

  const setPeriodo = (m: number, a: number) => {
    const next = new URLSearchParams(sp);
    next.set("mes", String(m));
    next.set("anio", String(a));
    setSp(next, { replace: true });
  };

  const q = useQuery({
    queryKey: ["analisis-consumo", anio, mes],
    queryFn: () => fetchAnalisisConsumo(anio, mes),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Análisis de consumo"
        icon={BarChart3}
        actions={
          <>
            <select
              aria-label="Mes"
              value={mes}
              onChange={(e) => setPeriodo(Number(e.target.value), anio)}
              className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            >
              {MESES.map((nombre, idx) => (
                <option key={idx} value={idx + 1}>
                  {nombre}
                </option>
              ))}
            </select>
            <input
              type="number"
              aria-label="Año"
              min={2020}
              max={2100}
              value={anio}
              onChange={(e) => setPeriodo(mes, Number(e.target.value))}
              className="w-24 rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            />
          </>
        }
      />

      {q.isPending && <Cargando />}
      {q.isError && (
        <ErrorCarga
          error={q.error}
          mensaje="Error al cargar el análisis."
          onReintentar={() => void q.refetch()}
        />
      )}

      {q.data && (
        <>
          <BalanceHidricoBand balance={q.data.balance_hidrico} />
          <SectoresBand sectores={q.data.sectores} periodo={q.data.periodo} />
          <DistribucionBand dist={q.data.distribucion} periodo={q.data.periodo} />
        </>
      )}
    </div>
  );
}

/* ─────────────────────────  Banda 1 · Balance hídrico  ──────────────────── */

function anfTone(pct: number | null): { text: string; bar: string } {
  if (pct == null) return { text: "text-neutral-500", bar: "bg-neutral-300" };
  if (pct >= 40) return { text: "text-rose-700", bar: "bg-rose-500" };
  if (pct >= 25) return { text: "text-amber-700", bar: "bg-amber-500" };
  return { text: "text-emerald-700", bar: "bg-emerald-500" };
}

function BalanceHidricoBand({ balance }: { balance: BalanceHidrico }): JSX.Element {
  return (
    <Card>
      <div className="mb-1 flex items-center gap-2">
        <Droplets className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-neutral-800">
          Balance hídrico — producido vs facturado
        </h3>
      </div>
      <p className="mb-4 max-w-3xl text-xs text-neutral-500">
        Agua No Facturada (ANF) = lo que sale de las fuentes menos lo que se
        consume medido. La diferencia son pérdidas: fugas de red, conexiones
        clandestinas, error de micromedición. Es un indicador global del comité.
      </p>

      {!balance.tiene_datos_produccion ? (
        <EmptyState
          icon={Droplets}
          tone="slate"
          title="Sin datos de producción"
          description="Carga la producción diaria de las fuentes en Continuidad → Producción para ver el balance hídrico y el % de Agua No Facturada."
        >
          <Link
            to="/continuidad"
            className="text-sm font-medium text-brand-700 hover:text-brand-800"
          >
            Ir a Continuidad →
          </Link>
        </EmptyState>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500">
                ANF último mes
              </div>
              <div
                className={`text-3xl font-bold ${anfTone(balance.anf_pct_actual).text}`}
              >
                {balance.anf_pct_actual != null
                  ? `${balance.anf_pct_actual}%`
                  : "—"}
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-neutral-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-brand-500" /> Producido
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-emerald-500" /> Facturado
              </span>
            </div>
          </div>
          <BalanceChart balance={balance} />
        </div>
      )}
    </Card>
  );
}

function BalanceChart({ balance }: { balance: BalanceHidrico }): JSX.Element {
  const max = Math.max(...balance.meses.map((m) => m.producido_m3), 1);
  return (
    <div className="flex items-end gap-3 overflow-x-auto pb-1">
      {balance.meses.map((m) => {
        const hProd = Math.round((m.producido_m3 / max) * 120);
        const hFact = Math.round((m.facturado_m3 / max) * 120);
        return (
          <div
            key={`${m.anio}-${m.mes}`}
            className="flex min-w-[68px] flex-1 flex-col items-center gap-1"
          >
            <div
              className={`text-xs font-semibold ${anfTone(m.anf_pct).text}`}
              title="Agua No Facturada"
            >
              {m.anf_pct != null ? `${m.anf_pct}%` : "—"}
            </div>
            <div className="flex h-[120px] items-end gap-1">
              <div
                className="w-4 rounded-t bg-brand-500"
                style={{ height: `${Math.max(2, hProd)}px` }}
                title={`Producido: ${m.producido_m3.toLocaleString("es-CL")} m³`}
              />
              <div
                className="w-4 rounded-t bg-emerald-500"
                style={{ height: `${Math.max(2, hFact)}px` }}
                title={`Facturado: ${m.facturado_m3.toLocaleString("es-CL")} m³`}
              />
            </div>
            <div className="text-xs capitalize text-neutral-500">{m.label}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────────────  Banda 2 · Por sector  ─────────────────────── */

function SectoresBand({
  sectores,
  periodo,
}: {
  sectores: SectorConsumo[];
  periodo: string;
}): JSX.Element {
  const max = Math.max(...sectores.map((s) => s.m3), 1);
  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-neutral-800">
          Consumo por sector · <span className="capitalize">{periodo}</span>
        </h3>
      </div>
      {sectores.length === 0 ? (
        <p className="text-sm text-neutral-500">Sin lecturas en el período.</p>
      ) : (
        <div className="space-y-2.5">
          {sectores.map((s) => (
            <div key={s.sector_id} className="flex items-center gap-3">
              <div className="w-32 shrink-0 truncate text-sm text-neutral-700" title={s.sector_nombre}>
                {s.sector_nombre}
              </div>
              <div className="flex-1">
                <div className="h-6 overflow-hidden rounded bg-neutral-100">
                  <div
                    className="flex h-full items-center rounded bg-brand-700 pl-2 text-xs font-medium text-white"
                    style={{ width: `${Math.max(6, (s.m3 / max) * 100)}%` }}
                  >
                    {s.m3.toLocaleString("es-CL")} m³
                  </div>
                </div>
              </div>
              <div className="w-24 shrink-0 text-right text-xs tabular-nums text-neutral-500">
                {formatCLP(s.monto)}
              </div>
              <div className="w-28 shrink-0 text-right text-xs tabular-nums text-neutral-500">
                {s.n_contratos} ctto · {s.consumo_promedio} m³/c
              </div>
              <div className="w-12 shrink-0 text-right text-xs tabular-nums text-neutral-500">
                {s.pct_del_total}%
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ────────────────────  Banda 3 · Distribución + top  ────────────────────── */

function DistribucionBand({
  dist,
  periodo,
}: {
  dist: DistribucionSocios;
  periodo: string;
}): JSX.Element {
  const maxBucket = Math.max(...dist.buckets.map((b) => b.n_socios), 1);
  const maxTop = Math.max(...dist.top.map((t) => t.consumo_m3), 1);
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      {/* Histograma */}
      <Card>
        <div className="mb-1 flex items-center gap-2">
          <Users className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-neutral-800">
            Distribución de consumo
          </h3>
        </div>
        <div className="mb-3 flex gap-4 text-xs text-neutral-500">
          <span>
            Promedio <strong className="text-neutral-700">{dist.promedio_m3} m³</strong>
          </span>
          <span>
            Mediana <strong className="text-neutral-700">{dist.mediana_m3} m³</strong>
          </span>
          <span>
            {dist.total_lecturas} lecturas · {periodo}
          </span>
        </div>
        <div className="flex h-40 items-end gap-2">
          {dist.buckets.map((b) => (
            <div key={b.label} className="flex flex-1 flex-col items-center gap-1">
              <div className="text-xs font-medium tabular-nums text-neutral-600">
                {b.n_socios}
              </div>
              <div
                className="w-full rounded-t bg-brand-500/80"
                style={{ height: `${Math.max(2, (b.n_socios / maxBucket) * 120)}px` }}
                title={`${b.label} m³: ${b.n_socios} socios`}
              />
              <div className="text-xs text-neutral-500">{b.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-1 text-center text-xs text-neutral-500">m³ consumidos</div>
      </Card>

      {/* Top consumidores */}
      <Card>
        <h3 className="mb-3 text-sm font-semibold text-neutral-800">
          Top 10 consumidores
        </h3>
        {dist.top.length === 0 ? (
          <p className="text-sm text-neutral-500">Sin lecturas en el período.</p>
        ) : (
          <div className="space-y-1.5">
            {dist.top.map((t, i) => (
              <div key={t.socio_id} className="flex items-center gap-2 text-sm">
                <span className="w-5 shrink-0 text-right text-xs text-neutral-500">
                  {i + 1}
                </span>
                <Link
                  to={`/socios/${t.socio_id}/cuenta-corriente`}
                  className="w-40 shrink-0 truncate font-medium text-neutral-800 hover:text-brand-700"
                  title={t.socio_nombre}
                >
                  {t.socio_nombre}
                </Link>
                <div className="flex-1">
                  <div className="h-4 overflow-hidden rounded bg-neutral-100">
                    <div
                      className="h-full rounded bg-amber-400"
                      style={{ width: `${(t.consumo_m3 / maxTop) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="w-16 shrink-0 text-right text-xs tabular-nums font-semibold text-neutral-700">
                  {t.consumo_m3} m³
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </section>
  );
}
